import { useState } from "react";

import {
  pluralCategory,
  useLocale,
  useT,
  type Locale,
} from "../../shared/i18n";
import {
  Banner,
  Card,
  GroupLabel,
  Icons,
  ListRow,
  Sheet,
} from "../../shared/ui";
import { controlClassName } from "../../shared/ui/controlClassName";
import { findZones } from "./zones";

const LANGUAGES: Locale[] = ["ru", "en"];

/** Time zone and interface language; the theme always follows Telegram. */
export function ProfileSettings({
  timezone,
  locale,
  onTimezone,
  onLocale,
}: {
  timezone: string | null | undefined;
  locale: Locale;
  onTimezone: (zone: string) => Promise<void>;
  onLocale: (locale: Locale) => Promise<void>;
}) {
  const t = useT();
  const { locale: uiLocale } = useLocale();
  const [open, setOpen] = useState<"timezone" | "language" | null>(null);
  const [query, setQuery] = useState("");
  const [failed, setFailed] = useState(false);
  const { total, zones } = findZones(query);
  const save = (run: () => Promise<void>) => {
    setFailed(false);
    void run()
      .then(() => {
        setOpen(null);
      })
      .catch(() => {
        setFailed(true);
      });
  };
  const chosen = <Icons.Check size={18} className="text-accent-fg" />;
  return (
    <section aria-label={t("profile.settings")} className="flex flex-col gap-2">
      <GroupLabel>{t("profile.settings")}</GroupLabel>
      {failed && <Banner tone="error">{t("profile.settingFailed")}</Banner>}
      <Card flush>
        <ul className="overflow-hidden rounded-xl [&>li:last-child>[data-row]]:after:hidden">
          <li>
            <ListRow
              kind="setting"
              title={t("profile.timezone")}
              trailing={timezone ?? "—"}
              chevron
              onClick={() => {
                setQuery("");
                setOpen("timezone");
              }}
            />
          </li>
          <li>
            <ListRow
              kind="setting"
              title={t("profile.language")}
              trailing={t(`language.${locale}`)}
              chevron
              onClick={() => {
                setOpen("language");
              }}
            />
          </li>
        </ul>
      </Card>
      <Sheet
        open={open === "timezone"}
        onOpenChange={(value) => {
          setOpen(value ? "timezone" : null);
        }}
        title={t("profile.timezone")}
      >
        <input
          type="search"
          aria-label={t("profile.timezoneSearch")}
          placeholder={t("profile.timezoneSearch")}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          className={controlClassName}
        />
        <p className="py-2 text-caption text-fg-muted">
          {t("sections.found")} {total}{" "}
          {t(`count.zone.${pluralCategory(uiLocale, total)}`)}
        </p>
        <ul>
          {zones.map((zone) => (
            <li key={zone.id}>
              <ListRow
                kind="setting"
                title={zone.id}
                subtitle={`${zone.offset} · ${t("profile.now")} ${zone.time}`}
                trailing={zone.id === timezone ? chosen : undefined}
                onClick={() => {
                  save(() => onTimezone(zone.id));
                }}
              />
            </li>
          ))}
        </ul>
      </Sheet>
      <Sheet
        open={open === "language"}
        onOpenChange={(value) => {
          setOpen(value ? "language" : null);
        }}
        title={t("profile.languageTitle")}
      >
        <ul>
          {LANGUAGES.map((option) => (
            <li key={option}>
              <ListRow
                kind="setting"
                title={t(`language.${option}`)}
                trailing={option === locale ? chosen : undefined}
                onClick={() => {
                  save(() => onLocale(option));
                }}
              />
            </li>
          ))}
        </ul>
      </Sheet>
    </section>
  );
}
